import Form from '../Form';
import JraAssociate from './JraModules/JraAssociate';
import JraChildren from './JraModules/JraChildren';
import JraPrimary from './JraModules/JraPrimary';
import JraSecondary from './JraModules/JraSecondary';
import JraAddressLookup from './JraModules/JraAddressLookup';
import JraTellUsMore from './JraModules/JraTellUsMore';
import JraMembershipOptions from './JraModules/JraMembershipOptions';
import JraCampingUnit from './JraModules/JraCampingUnit';
import JraJoinMembershipOptions from './JraModules/JraJoinMembershipOptions';
import JraJoinPaymentOptions from './JraModules/JraJoinPaymentOptions';
import JraShowJoin from './JraModules/JraShowJoin';
import JraPayments from './JraModules/JraPayments';
import JraDateOfBirth from './JraModules/JraDateOfBirth';
import Utils from '../../../util/Utils';
import { leaveFloat } from '../../../util/regex';
import {
  capitalizeInputValue,
  changeInputValueToUppercase,
  isCustomValidInput,
  resetInputValue,
} from './helpers';
import {
  SECTION_EXPANDED_CLASS,
  OVERSEAS_CLASS,
  LOCATION_UK,
  SESSION_FORM_DATA_KEY,
  SESSION_FORM_DATA_VERSION,
  moduleTypes,
} from './constants';
import JraCustomise from './JraModules/JraCustomise';

export default class JraForm extends Form {
  constructor(element) {
    super(element);

    this.element = element;
    this.toggleClass = 'active';

    this.initJraForm();
    this.checkToDecodeSessionData();
    // eslint-disable-next-line no-underscore-dangle
    window.__jraform = this;
  }


  initJraForm() {
    this.setJraFormType();
    this.paymentItems = [];
    this.expandableSections = [...this.element.querySelectorAll('[data-jra-form-expandable-section]')];
    this.paymentSummaryItems = [];
    this.paymentSummaryItemsContainer = this.element.querySelector('[data-jra-form-summary-additional-items]');
    this.paymentSummaryTotal = null;
    this.paymentSummaryTotalContainer = this.element.querySelector('[data-jra-form-summary-total]');
    this.paymentSummaryMembershipPrice = this.element.querySelector('[data-jra-form-summary-membership-price]');
    this.paymentSummaryMembershipType = this.element.querySelector('[data-jra-form-summary-membership-type]');
    this.paymentSummaryMembershipLogo = this.element.querySelector('[data-jra-form-summary-membership-payment-logo]');

    this.countryNameInput = this.element.querySelector('[data-jra-form-country-name-input]');
    this.countryCodeInput = this.element.querySelector('[data-jra-form-country-code-input]');

    this.postcodeInput = this.element.querySelector('[data-form-postcode-input]');
    this.autoCapTextInputs = [...this.element.querySelectorAll('[data-form-auto-capitalize]')];
    this.termsConditionsCheckbox = this.element.querySelector('[data-jra-form-terms-conditions-checkbox]');

    this.loqateApiKey = this.element.dataset.loqateApiKey;

    if (this.loqateApiKey) {
      this.initLoqate();
    }

    // must be done after the above properties are set.
    this.initModules();

    this.inputs.forEach((input) => {
      isCustomValidInput(input);
      input.addEventListener('change', () => isCustomValidInput(input));
      input.addEventListener('keyup', () => isCustomValidInput(input));
    });

    this.autoCapTextInputs.forEach((input) => {
      input.addEventListener('keyup', () => capitalizeInputValue(input));
    });

    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('keyup', () => changeInputValueToUppercase(this.postcodeInput));
    }

    if (this.termsConditionsCheckbox) {
      if (this.termsConditionsCheckbox.checked) {
        resetInputValue(this.termsConditionsCheckbox);
      }
    }

    this.expandableSections.forEach((expandableSection) => {
      const inputs = [...expandableSection.querySelectorAll('[data-form*="input"]')];

      const sectionObserver = new MutationObserver((mutations) => {
        [...mutations].forEach(() => {
          inputs.forEach((input) => {
            if (expandableSection.classList.contains(SECTION_EXPANDED_CLASS) === false) {
              resetInputValue(input);
            }
            isCustomValidInput(input);
          });
        });
      });
      sectionObserver.observe(expandableSection, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', this.onJraFormSubmit);
    }
  }


  initLoqate() {
    this.loadLoqateStylesheet();
    this.loadLoqateScript();
  }


  loadLoqateStylesheet() {
    const loqateStylesheet = document.createElement('link');
    loqateStylesheet.rel = 'stylesheet';
    loqateStylesheet.type = 'text/css';
    loqateStylesheet.href = `https://services.postcodeanywhere.co.uk/css/address-3.91.css?key=${this.loqateApiKey}`;
    document.head.appendChild(loqateStylesheet);
  }


  loadLoqateScript() {
    const loqateScript = document.createElement('script');
    loqateScript.type = 'text/javascript';
    loqateScript.src = `https://services.postcodeanywhere.co.uk/js/address-3.91.js?key=${this.loqateApiKey}`;
    document.head.appendChild(loqateScript);
    loqateScript.addEventListener('load', () => this.initLoqateCode());
  }


  initLoqateCode() {
    const fields = [{
      element: 'country',
      field: 'CountryName',
      mode: pca.fieldMode.COUNTRY,
    }];
    const control = new pca.Address(fields, { key: this.loqateApiKey, setCountryByIP: false });

    control.listen('country', (address) => {
      if (this.countryNameInput) {
        this.countryNameInput.dataset.jraFormCountryName = this.countryNameInput.value;
        this.countryCodeInput.value = address.iso2;
        const event = document.createEvent('Event');
        event.initEvent('change', true, true);
        this.countryCodeInput.dispatchEvent(event);
        isCustomValidInput(this.countryNameInput);

        const countryDefaultValue = this.countryNameInput.defaultValue !== '';
        const countryIsNotDefaultValue = this.countryNameInput.value
          !== this.countryNameInput.defaultValue;

        // ensure that the address inputs do not reset on load of amend as country
        // default value will exist.
        if (
          !countryDefaultValue
          || (countryDefaultValue && countryIsNotDefaultValue)
        ) {
          if (this.JraAddressLookup) {
            this.JraAddressLookup.resetInputsOnLocateUpdate();
          }
        }
      }
    });

    if (this.countryNameInput && (this.countryNameInput.defaultValue !== '' || this.countryNameInput.value !== '')) {
      // country is set in view for amend, need to trigger change event to make sure
      // loqate code to map country code runs in this scenario.
      const event = document.createEvent('Event');
      event.initEvent('change', true, true);
      this.countryNameInput.dispatchEvent(event);
    }
  }


  initModules() {
    // Primary
    const primaryElement = this.element.querySelector('[data-jra-primary-module]');
    if (primaryElement) {
      this.jraPrimary = new JraPrimary(primaryElement, moduleTypes.PRIMARY, this.isJoinForm);
    }


    // Secondary
    const secondaryElement = this.element.querySelector('[data-jra-secondary-module]');
    if (secondaryElement) {
      this.jraSecondary = new JraSecondary(secondaryElement, moduleTypes.SECONDARY);

      if (this.jraPrimary) {
        this.jraPrimary.on(JraPrimary.PRIMARY_EMAIL_CHANGE_EVENT, () => {
          if (this.jraSecondary.secondaryEmailInput.value !== '') {
            isCustomValidInput(this.jraSecondary.secondaryEmailInput);
          }
        });
      }
    }


    // Children
    const childrenElement = this.element.querySelector('[data-jra-children-module]');
    if (childrenElement) {
      this.jraChildren = new JraChildren(childrenElement, moduleTypes.CHILDREN);
    }


    // Tell Us More
    const tellUsMoreElement = this.element.querySelector('[data-jra-tell-us-more]');
    if (tellUsMoreElement) {
      this.jraTellUsMore = new JraTellUsMore(tellUsMoreElement, moduleTypes.TELL_US_MORE);
    }

    if (this.isJoinForm) {
      // if on join form then membership options are split into membership type & payment type
      const joinMembershipOptionsElement = this.element.querySelector('[data-jra-join-membership-options]');
      if (joinMembershipOptionsElement) {
        this.jraJoinMembershipOptions
          = new JraJoinMembershipOptions(joinMembershipOptionsElement, moduleTypes.FORMAT);
      }

      // this.jraMembership here is essentially the same as the membership options used on renew,
      // the options are the same, they just display differently
      const joinPaymentOptionsElement = this.element.querySelector('[data-jra-join-payment-options]');
      if (joinPaymentOptionsElement) {
        this.jraMembership
          = new JraJoinPaymentOptions(joinPaymentOptionsElement, moduleTypes.MEMBERSHIP);
      }

      if (this.jraJoinMembershipOptions && this.jraMembership) {
        // on subscription change (digital/paper) display payment options for subscription type
        this.jraJoinMembershipOptions.on(
          JraJoinMembershipOptions.JOIN_SUBSCRIPTION_TYPE_CHANGE_EVENT,
          this.jraMembership.togglePaymentOptionsDisplay,
        );
        this.jraJoinMembershipOptions.on(
          JraJoinMembershipOptions.JOIN_MEMBERSHIP_TYPE_CHANGE_EVENT,
          this.onChangeMembership,
        );
        // on membership type change (overseas/uk), toggle the payment options that display
        this.jraJoinMembershipOptions.on(
          JraJoinMembershipOptions.JOIN_MEMBERSHIP_TYPE_CHANGE_EVENT,
          this.jraMembership.toggleMembershipType,
        );
        this.jraMembership.on(JraJoinPaymentOptions.EVENT_CHANGE, this.onChangeMembership);
      }
      // generate initial payment total markup to add required message for membership
      this.generatePaymentTotalMarkup();
    } else {
      // Membership options for renew
      const membershipOptionsElement = this.element.querySelector('[data-jra-membership-options]');
      if (membershipOptionsElement) {
        this.jraMembership = new JraMembershipOptions(
          membershipOptionsElement,
          moduleTypes.MEMBERSHIP,
        );
        this.jraMembership.on(JraMembershipOptions.EVENT_CHANGE, this.onChangeMembership);

        if (this.form.classList.contains(OVERSEAS_CLASS)) {
          // on renew, if overseas the class will be applied in JRARenew\index.cshtml
          this.jraMembership.presetOverseas(); // this preselects overseas and activates event above
        } else if (!this.form.classList.contains(OVERSEAS_CLASS)) {
          this.jraMembership.presetUK(); // this preselects the UK and activates event above
        }
      } else {
        // set the value of the country select on amend
        let location = LOCATION_UK;
        if (this.form.classList.contains(OVERSEAS_CLASS)) {
          location = '';
        }
        this.onChangeMembership(location);
      }
    }


    // All Date Of Birth's Controls Across Modules
    const dateOfBirthElements = [...this.element.querySelectorAll('[data-jra-date-of-birth]')];
    this.dateOfBirthElements = [];

    if (dateOfBirthElements.length > 0) {
      dateOfBirthElements.forEach((dateOfBirthElement, index) => {
        this.dateOfBirthElements[index] = new JraDateOfBirth(dateOfBirthElement);
      });
    }


    // Address Lookup (Contact Details)
    const addressLookupElement = this.element.querySelector('[data-jra-address-lookup]');
    if (addressLookupElement) {
      this.addressLookup = new JraAddressLookup(addressLookupElement, moduleTypes.CONTACT);
    }


    // Camping Unit
    const campingUnitElement = this.element.querySelector('[data-jra-camping-unit]');
    if (campingUnitElement) {
      this.jraCampingUnit = new JraCampingUnit(campingUnitElement, moduleTypes.CAMPING_UNIT);
    }


    // Associate
    const associateElement = this.element.querySelector('[data-jra-associate-module]');
    if (associateElement) {
      this.jraAssociate = new JraAssociate(associateElement, moduleTypes.ASSOCIATE);
      this.jraAssociate.on(
        JraAssociate.ASSOCIATE_TOGGLE_EVENT,
        this.generatePaymentSummaryItemsMarkup,
      );
      this.jraAssociate.on(JraAssociate.ASSOCIATE_ADD_EVENT, (event) => {
        this.paymentSummaryItems.unshift({ key: event.key, value: event.value });
      });
      this.jraAssociate.on(JraAssociate.ASSOCIATE_REMOVE_EVENT, (event) => {
        this.paymentSummaryItems = this.paymentSummaryItems.filter(i => i.key !== event.key);
      });
    }


    // Customise Membership
    const customiseElement = this.element.querySelector('[data-jra-customise-module]');
    if (customiseElement) {
      this.jraCustomise = new JraCustomise(customiseElement, moduleTypes.CUSTOMISE);
      this.jraCustomise.on(JraCustomise.CUSTOMISE_TOGGLE_EVENT, this.toggleCustomisePaymentItems);
    }


    // Show Join
    const showJoinElement = this.element.querySelector('[data-jra-show-join]');
    if (showJoinElement) this.showJoin = new JraShowJoin(showJoinElement);


    // Payments (Direct Debit Only Page)
    const paymentsSection = this.element.dataset.jraPayments ? this.element : this.element.querySelector('[data-jra-payments]');
    if (paymentsSection) this.payments = new JraPayments(paymentsSection);
  }


  encodeInToSessionData() {
    const time = new Date().getTime();
    const version = SESSION_FORM_DATA_VERSION;

    const modules = {};

    if (this.jraJoinMembershipOptions) this.jraJoinMembershipOptions.encode(modules);
    if (this.jraMembership) this.jraMembership.encode(modules);
    if (this.jraPrimary) this.jraPrimary.encode(modules);
    if (this.jraSecondary) this.jraSecondary.encode(modules);
    if (this.jraCampingUnit) this.jraCampingUnit.encode(modules);
    if (this.jraChildren) this.jraChildren.encode(modules);
    if (this.jraTellUsMore) this.jraTellUsMore.encode(modules);
    if (this.jraAssociate) this.jraAssociate.encode(modules);
    if (this.jraCustomise) this.jraCustomise.encode(modules);
    if (this.addressLookup) this.addressLookup.encode(modules);

    const stringifiedData = JSON.stringify({
      time,
      version,
      modules,
    });

    window.sessionStorage.setItem(SESSION_FORM_DATA_KEY, stringifiedData);
  }

  decode() {
    if (this.jraJoinMembershipOptions) this.jraJoinMembershipOptions.decode();
    if (this.jraMembership) this.jraMembership.decode();
    if (this.jraPrimary) this.jraPrimary.decode();
    if (this.jraSecondary) this.jraSecondary.decode();
    if (this.jraCampingUnit) this.jraCampingUnit.decode();
    if (this.jraChildren) this.jraChildren.decode();
    if (this.jraTellUsMore) this.jraTellUsMore.decode();
    if (this.jraAssociate) this.jraAssociate.decode();
    if (this.jraCustomise) this.jraCustomise.decode();
    if (this.addressLookup) this.addressLookup.decode();
  }

  onChangeMembership = (location, hiddenInput) => {
    if (hiddenInput) {
      isCustomValidInput(hiddenInput);
    }

    if (this.countryNameInput && !this.isRenewForm && this.countryNameInput.defaultValue === '') {
      // keep members country & country code values on renew
      if (location === 'UK') {
        this.countryCodeInput.value = 'GB';
        this.countryNameInput.value = 'United Kingdom';
        this.countryNameInput.dataset.jraFormCountryName = this.countryNameInput.value;
      } else {
        this.countryCodeInput.value = '';
        this.countryNameInput.value = '';
        this.countryNameInput.dataset.jraFormCountryName = '';
      }

      isCustomValidInput(this.countryNameInput);
    }

    if (hiddenInput) {
      this.generatePaymentTotalMarkup();
    }
  }

  onJraFormSubmit = (e) => {
    const RECAPTCHA_KEY = document.getElementById('GoogleRecaptchaKey').dataset.googleRecaptchaKey

    if (RECAPTCHA_KEY) {
      e.preventDefault()
      
      const $this = this

      grecaptcha.ready(function () {
        grecaptcha.execute(RECAPTCHA_KEY, { action: 'submit' }).then((token) => {
          var input = document.createElement("input");
          input.type = "text";
          input.name = "RecaptchaToken"
          input.value = token
          input.hidden = true
          $this.form.appendChild(input);
          document.getElementById('GoogleRecaptchaKey').remove()
          $this.form.submit()
        });
      });
    } else {
      // Encode inputted data.
      this.encodeInToSessionData();
    }
  }

  checkToDecodeSessionData() {
    // must be either join or renew
    if (!this.isJoinForm && !this.isRenewForm) return;

    const sessionString = window.sessionStorage.getItem(SESSION_FORM_DATA_KEY);
    if (sessionString) {
      this.decode();
    }
  }

  toggleCustomisePaymentItems = ({ itemKey, itemValue }) => {
    const previousIndex =
      this.paymentSummaryItems.findIndex(paymentSummaryItem => paymentSummaryItem.key === itemKey);

    if (previousIndex === -1) {
      // add
      this.paymentSummaryItems.push({
        key: itemKey,
        value: parseFloat(leaveFloat(itemValue)),
      });
    } else {
      // remove
      this.paymentSummaryItems.splice(previousIndex, 1);
    }

    this.generatePaymentSummaryItemsMarkup();
  }


  generatePaymentSummaryItemsMarkup = () => {
    let paymentSummaryItemsHtml = '';

    this.paymentSummaryItems.forEach((item) => {
      paymentSummaryItemsHtml += `
        <div class="JraSummary_item">
            <h4 class="JraSummary_itemTitle">${item.key}</h4>
            <h4 class="JraSummary_itemPrice">${Utils.toPrice(item.value)}</h4>
        </div>
      `;
    });

    this.paymentSummaryItemsContainer.innerHTML = paymentSummaryItemsHtml;
    this.generatePaymentTotalMarkup();
  }


  generatePaymentTotalMarkup() {
    let membershipPrice;
    let paymentTotal;
    if (this.jraMembership.membershipPrice) {
      membershipPrice = Number.parseFloat(this.jraMembership.membershipPrice);
      paymentTotal = 0 + membershipPrice;
    }

    this.paymentSummaryItems.forEach((item) => {
      paymentTotal += item.value;
    });

    if (this.jraMembership.membershipPaymentLogoString) {
      this.paymentSummaryMembershipLogo.innerHTML = this.jraMembership.membershipPaymentLogoString;
    }

    if (this.jraMembership.membershipPrice) {
      this.paymentSummaryMembershipPrice.innerHTML = Utils.toPrice(membershipPrice);
      this.paymentSummaryTotalContainer.innerHTML = Utils.toPrice(paymentTotal);
    }
    this.paymentSummaryMembershipType.innerHTML = this.jraMembership.membershipName;
  }

  setJraFormType = () => {
    this.isAmendForm = false;
    this.isRenewForm = false;
    this.isJoinForm = false;
    const joinString = 'Join';
    const amendString = 'Amend';
    const renewString = 'Renew';
    this.jraFormType = this.element.dataset.jraFormType;

    if (this.jraFormType === joinString) {
      this.isJoinForm = true;
    } else if (this.jraFormType === amendString) {
      this.isAmendForm = true;
    } else if (this.jraFormType === renewString) {
      this.isRenewForm = true;
    }
  }
}
